import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery
} from '@tanstack/react-query'
import {
  fetchLikeDetails,
  fetchUserProfileDetails,
  fetchUserUpdateDetails,
  deleteContentItems,
  fetchUserDashboardDetails,
  fetchUserDashboard,
  bulkDeleteContent,
  fetchFilterOptions,
  fetchUserDrafts,
  fetchUserHomeDrafts,
  fetchBannerData,
  updateSelectedImage,
  deleteDrafts
} from '../api/profilePageApi'
import { fetchSavedContent, saveContent } from '../api/savedcontentApi'
import {
  fetchTopCreatorList,
  fetchTopCreatorDetails,
  fetchCreatorEconomyData
} from '../api/topCreatorApi'

import {
  fetchFollowerDetails,
  fetchFollowerList,
  fetchFollowDetails,
  unfollowUser,
  fetchSuggestedUsers
} from '../api/followerApi'
import { likeContent, unlikeContent, fetchUserLikes } from '../api/discoverApi'
import { verifyEmail,verifyConset} from '../api/loginApi'
import { QUERY_KEY } from '../utils'

export const useTopCreatorList = (
  queryParams = {},
  options = {}
) => {
  return useInfiniteQuery({
    queryKey: ['GET_TOP_CREATOR_LIST', queryParams],
    initialPageParam: 0,

    queryFn: async ({ pageParam }) => {
      return fetchTopCreatorList(pageParam, 20, queryParams)
    },

    getNextPageParam: (lastPage) =>
      lastPage?.data?.next,

    staleTime: 0,
    gcTime:0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
    ...options
  })
}

export const useFetchCreatorEconomyList = (
  queryParams = {},
  options = {}
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY.GET_CREATOR_ECONOMY_LIST, queryParams],
    initialPageParam: 0,

    queryFn: async ({ pageParam }) => {
      return fetchCreatorEconomyData(pageParam, 20, queryParams)
    },

    getNextPageParam: (lastPage) => {
      const next = lastPage?.data?.data?.next ?? lastPage?.data?.next ?? lastPage?.next
      return next != null ? next : undefined
    },

    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options
  })
}

export const useFetchBannerImage = (
  type = 'all',
  queryParams = {},
  options = {}
) => {
  return useInfiniteQuery({
    queryKey: [
      QUERY_KEY.BANNER_IMAGE_LIST,
      type
    ],

    initialPageParam: 0,

    queryFn: async ({ pageParam }) => {
      return fetchBannerData(
        100,
        pageParam,
        type
      )
    },

    getNextPageParam: (lastPage) => {
      return lastPage?.data?.next
    },

    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,

    ...options
  })
}


export const useSubmitBannerImage = ({ onSuccess } = {}) => {
  return useMutation({
    mutationKey: ['SUBMIT_BANNER_IMAGE'],

    mutationFn: async (data) => {
      if (!data) {
        throw new Error('Banner data is required')
      }

      return await updateSelectedImage(data)
    },

    onSuccess: (response) => {
      onSuccess?.(response)
    }
  })
}
export const useUserProfile = (user_id, options = {}) => {
  let accessToken = localStorage.getItem('access')
  return useQuery({
    queryKey: ['GET_USER_PROFILE', user_id],
    queryFn: async () => {
      if (!user_id) throw new Error('User ID is required')
      return await fetchUserProfileDetails(user_id)
    },
    enabled: !!user_id && !!accessToken && (options.enabled ?? true),
    retry: 1,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options
  })
}

export const useUserDashboardDetail = (user_id, options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEY.GET_USER_DASHBOARD_DETAIL, user_id],
    queryFn: async () => {
      if (!user_id) throw new Error('User ID is required')
      return await fetchUserDashboardDetails(user_id)
    },

    enabled: !!user_id,

    staleTime: 0, // make data stale immediately
    refetchOnMount: true, // refetch every time page opens
    refetchOnWindowFocus: false, // optional

    retry: 1,
    ...options
  })
}
export const useUserUpdate = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['GET_USER_UPDATE'],
    mutationFn: async (formData) => {
      return await fetchUserUpdateDetails(formData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['GET_USER_DASHBOARD'])
    },
    ...options
  })
}

export const useUserDashboard = (
  user_id,
  verification_status = 'pending',
  content_type = ['video', 'image', 'audio'],
  normalizedTab,
  sort_by = 'newest_first',
  created_with = [],
  options = {},
  limit = 12
) => {
  const queryClient = useQueryClient()
  return useInfiniteQuery({
    queryKey: [
      QUERY_KEY.GET_USER_DASHBOARD,
      user_id,
      verification_status,
      content_type,
      normalizedTab,
      sort_by,
      created_with,
      limit
    ],

    queryFn: async ({ pageParam = 0 }) => {
      if (!user_id) throw new Error('User ID is required')

      let response
      if (verification_status === 'saved') {
        response = await fetchSavedContent(
          user_id,
          content_type,
          limit,
          pageParam,
          sort_by,
          created_with
        )
      } else if (verification_status === 'drafts') {
        response = await fetchUserDrafts(content_type,sort_by,created_with ,limit, pageParam)
      } else if (verification_status === 'pending') {
        response = await fetchUserDashboard(
          user_id,
          verification_status,
          content_type,
          limit,
          pageParam,
          sort_by,
          created_with
        )
      } else {
        response = await fetchUserDashboardDetails(
          user_id,
          content_type,
          limit,
          pageParam,
          sort_by,
          created_with,
          verification_status
        )
      }

      // Sync fresh followers/following/creations counts to GET_USER_DASHBOARD_DETAIL query cache
      if (response?.followers !== undefined || response?.user_details) {
        queryClient.setQueryData(
          [QUERY_KEY.GET_USER_DASHBOARD_DETAIL, user_id],
          (old) => {
            if (!old) return old
            return {
              ...old,
              followers: response.followers ?? old.followers,
              following: response.following ?? old.following,
              creations: response.creations ?? old.creations,
              user_info: response.user_info ?? old.user_info
            }
          }
        )
      }

      // Map the response to the structure expected by the Dashboard component
      if (response?.success && response?.data) {
        let items = []
        if (verification_status === 'saved') {
          // response.data IS the array of saved content
          const rawItems = Array.isArray(response.data)
            ? response.data
            : response.data?.saved_content || []

          // Filter by content_type if it is a single string. If it is an array, it is likely already filtered by the API.
          if (content_type && typeof content_type === 'string') {
            items = rawItems.filter((item) => {
              if (content_type === 'video') {
                return (
                  item.content_type === 'video' || item.content_type === 'post'
                )
              }
              return item.content_type === content_type
            })
          } else {
            // Already filtered or no specific filter needed
            items = rawItems
          }
        } else if (verification_status === 'drafts') {
          items = response?.data?.drafts || response?.drafts || []
        } else {
          // Check if tab-specific results already exist in a nested structure
          const tabData =
            response.data?.[normalizedTab] || response[normalizedTab]
          if (tabData && Array.isArray(tabData.results)) {
            items = tabData.results
          } else if (Array.isArray(content_type)) {
            // Use the original flat API order (latest_creations) if available; avoids re-combining in a fixed type order
            const originalOrder =
              response.latest_creations ||
              response.data?.latest_creations ||
              response.data?.data

            if (Array.isArray(originalOrder) && originalOrder.length > 0) {
              items = originalOrder
            } else {
              // Fallback: combine typed sub-arrays
              const video =
                response.latest_video || response.data?.latest_video || []
              const audio =
                response.latest_audio || response.data?.latest_audio || []
              const photo =
                response.latest_images || response.data?.latest_images || []
              items = [...video, ...audio, ...photo]
            }
          } else {
            // Single content type mapping
            if (content_type === 'video') {
              items = response.latest_video || response.data?.latest_video || []
            } else if (content_type === 'audio') {
              items = response.latest_audio || response.data?.latest_audio || []
            } else {
              items = response.latest_images || response.data?.latest_images || []
            }
          }
        }


        // Logic now prioritizes explicit next values, but falls back to meta.total_pages
        // if items.length matches limit, we assume more might exist regardless of metadata (some APIs are buggy)
        const nextValue =
          response.data?.next !== undefined && response.data?.next !== null
            ? response.data.next
            : response.data?.[normalizedTab]?.next !== undefined &&
              response.data?.[normalizedTab]?.next !== null
              ? response.data[normalizedTab].next
              : response.meta?.page < response.meta?.total_pages ||
                items.length >= limit
                ? (pageParam || 0) + (response.meta?.per_page || limit)
                : null

        return {
          ...response,
          data: {
            ...(response.data || {}),
            [normalizedTab]: {
              results: items,
              count:
                response.meta?.total_records ??
                (response.data?.[normalizedTab]?.count ||
                  response.data?.count ||
                  items.length),
              next: nextValue
            }
          }
        }
      }

      return response
    },

    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage?.data?.[normalizedTab]?.next
      if (nextOffset === null || nextOffset === undefined) {
        return undefined
      }
      return Number(nextOffset)
    },
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
    ...options
  })
}

export const useEmailVerify = (token, options = {}) => {
  return useQuery({
    queryKey: ['GET_EMAIL_VERIFY', token],
    queryFn: async () => {
      if (!token) throw new Error('Token is required')
      return await verifyEmail(token)
    },
    ...options
  })
}

export const useConsentVerify = (options = {}) => {
  return useQuery({
    queryKey: ['GET_CONSENT_VERIFY'],
    queryFn: async () => {
      return await verifyConset()
    },
    ...options
  })
}

export const useFollowerDetails = (user_id, follower_id, options = {}) => {
  return useQuery({
    queryKey: ['GET_FOLLOWER_DETAILS', user_id, follower_id],
    queryFn: async () => {
      if (!user_id && !follower_id)
        throw new Error('User ID && Follower ID is required')
      const resp = await fetchFollowerList(user_id, follower_id)
      const data = resp?.data || resp
      return data?.followers || data || []
    },
    enabled: !!user_id && !!follower_id,
    ...options
  })
}

export const useLikeMutation = (isUGC) => {
  return useMutation({
    mutationKey: ['LIKE_ACTION'],
    mutationFn: async (content_id) => {
      if (!content_id) throw new Error('Content ID is required')
      return await likeContent(content_id, isUGC)
    }
  })
}

export const useUnlikeMutation = (isUGC) => {
  return useMutation({
    mutationKey: ['UNLIKE_ACTION'],
    mutationFn: async (content_id) => {
      if (!content_id) throw new Error('Content ID is required')
      return await unlikeContent(content_id, isUGC)
    }
  })
}

export const useUserLikesQuery = (token, options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEY.GET_USER_LIKES, token],
    queryFn: async () => {
      if (!token) throw new Error('No token provided')
      return await fetchUserLikes(token)
    },
    enabled: !!token,
    ...options
  })
}

export const useFollowMutation = (isUGC, options = {}) => {
  const finalOptions = typeof isUGC === 'object' ? isUGC : options
  const finalIsUGC = typeof isUGC === 'object' ? false : isUGC
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['FOLLOW_ACTION'],
    mutationFn: async (params) => {
      let user_id, follower_id
      if (Array.isArray(params)) {
        ;[user_id, follower_id] = params
      } else if (params && typeof params === 'object') {
        user_id = params.user_id
        follower_id = params.follower_id
      } else {
        throw new Error('Invalid parameters for follow mutation')
      }
      if (!user_id || !follower_id)
        throw new Error('User ID and Follower ID are required')
      return await fetchFollowerDetails(user_id, follower_id, finalIsUGC)
    },
    ...finalOptions,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_USER_DASHBOARD_DETAIL]
      })
      finalOptions.onSuccess?.(data, variables, context)
    }
  })
}

export const useFollowDetails = (user_id, follower_id, options = {}) => {
  return useQuery({
    queryKey: ['GET_FOLLOW_DETAILS', user_id, follower_id],
    queryFn: async () => {
      if (!user_id || !follower_id)
        throw new Error('User ID and Follower ID are required')
      const resp = await fetchFollowDetails(user_id, follower_id)
      return resp?.data || resp
    },
    enabled: !!user_id && !!follower_id,
    ...options
  })
}

export const useSuggestedUsers = (viewUserId, options = {}) => {
  return useQuery({
    queryKey: ['SUGGESTED_USERS', viewUserId],
    queryFn: async () => {
      return await fetchSuggestedUsers({ user_id: viewUserId, limit: 20, source_limit: 30 })
    },
    enabled: false,
    ...options
  })
}

export const useUnfollowMutation = (isUGC, options = {}) => {
  const finalOptions = typeof isUGC === 'object' ? isUGC : options
  const finalIsUGC = typeof isUGC === 'object' ? false : isUGC
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['UNFOLLOW_ACTION'],
    mutationFn: async (params) => {
      let user_id, follower_id
      if (Array.isArray(params)) {
        ;[user_id, follower_id] = params
      } else if (params && typeof params === 'object') {
        user_id = params.user_id
        follower_id = params.follower_id
      } else {
        throw new Error('Invalid parameters for unfollow mutation')
      }
      if (!user_id || !follower_id)
        throw new Error('User ID and Follower ID are required')
      return await unfollowUser(user_id, follower_id, finalIsUGC)
    },
    ...finalOptions,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_USER_DASHBOARD_DETAIL]
      })
      finalOptions.onSuccess?.(data, variables, context)
    }
  })
}

export const useTopCreatorDetails = (creatorId, options = {}) => {
  return useQuery({
    queryKey: ['GET_TOP_CREATOR_DETAILS', creatorId],
    queryFn: async () => {
      try {
        return await fetchTopCreatorDetails(creatorId)
      } catch (error) {
        throw error
      }
    },
    enabled: !!creatorId,
    ...options
  })
}

// Delete content for Published / Not Yet Published tabs
export const useDeleteContentMutation = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['DELETE_CONTENT'],
    mutationFn: async (ids) => {
      const contentIds = Array.isArray(ids) ? ids : [ids]
      if (contentIds.length === 0) throw new Error('Content IDs are required')

      const results = []
      for (const id of contentIds) {
        if (!id) continue

        const resp = await deleteContentItems(id)
        results.push(resp)
      }
      return results
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['GET_USER_DASHBOARD'] })
      queryClient.invalidateQueries({ queryKey: ['GET_SAVED_CONTENT'] })
      if (options.onSuccess) options.onSuccess(data, variables)
    },
    onError: (error, variables) => {
      if (options.onError) options.onError(error, variables)
    },
    ...options
  })
}

// Bulk delete mutation for Published section (Social API)
export const useBulkDeleteMutation = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['BULK_DELETE_CONTENT'],
    mutationFn: async (ids) => {
      if (!ids || (Array.isArray(ids) && ids.length === 0))
        throw new Error('Content IDs are required')
      return await bulkDeleteContent(ids)
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['GET_USER_DASHBOARD'] })
      queryClient.invalidateQueries({ queryKey: ['GET_SAVED_CONTENT'] })
      if (options.onSuccess) options.onSuccess(data, variables)
    },
    onError: (error, variables) => {
      if (options.onError) options.onError(error, variables)
    },
    ...options
  })
}

export const useFilterOptions = (options = {}) => {
  return useQuery({
    queryKey: ['GET_FILTER_OPTIONS'],
    queryFn: async () => {
      return await fetchFilterOptions()
    },
    staleTime: 5 * 60 * 1000,
    ...options
  })
}

export const useDeleteDraftMutation = (options = {}) => {
  return useMutation({
    mutationKey: ['DELETE_DRAFTS'],
    mutationFn: async ({ userId, draftIds }) => {
      const response = await deleteDrafts(userId, draftIds)
      return response
    },
    ...options
  })
}
